import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useItem} from "../hooks/useItem";
import {useCartContext} from "../contexts/CartContext";
import {toast} from "react-toastify";
import HtmlContent from "../components/HtmlContent";
import ItemImage from "../components/ItemImage";

const findVariantByValues = (variants, values) =>
    variants.find((variant) =>
        variant.properties.every((property) => values[property.name] === property.itemPropertyValueId)
        && variant.properties.length === Object.keys(values).length
    ) || null;

const ShopItemPage = () => {
    const {itemUuid} = useParams();
    const {item} = useItem(itemUuid);
    const {addToCart} = useCartContext();
    const navigate = useNavigate();

    const [selectedValues, setSelectedValues] = useState({});
    const [quantity, setQuantity] = useState(1);
    const [heroImage, setHeroImage] = useState(null);

    const propertyGroups = useMemo(() => {
        if (!item) {
            return [];
        }

        const groups = new Map();

        item.variants.forEach((variant) => {
            variant.properties.forEach((property) => {
                if (!groups.has(property.name)) {
                    groups.set(property.name, new Map());
                }
                groups.get(property.name).set(property.itemPropertyValueId, property.value);
            });
        });

        return Array.from(groups.entries()).map(([name, valuesMap]) => ({
            name,
            values : Array.from(valuesMap.entries()).map(([itemPropertyValueId, value]) => ({itemPropertyValueId, value}))
        }));
    }, [item]);

    const thumbnails = useMemo(() => {
        if (!item) {
            return [];
        }

        return item.variants.flatMap((variant) =>
            variant.images.map((image) => ({
                itemVariantImageId : image.itemVariantImageId,
                url                : image.url,
                itemVariantId      : variant.itemVariantId,
                variantProperties  : variant.properties
            }))
        );
    }, [item]);

    useEffect(() => {
        if (!item || item.variants.length === 0) {
            return;
        }

        const firstVariant = item.variants[0];
        const initialSelection = {};
        firstVariant.properties.forEach((property) => {
            initialSelection[property.name] = property.itemPropertyValueId;
        });
        setSelectedValues(initialSelection);
        setQuantity(1);
        setHeroImage(firstVariant.images[0]?.url || item.image);
    }, [item]);

    const selectedVariant = useMemo(() => {
        if (!item) {
            return null;
        }

        return findVariantByValues(item.variants, selectedValues);
    }, [item, selectedValues]);

    if (!item) {
        return <div>Loading...</div>;
    }

    const handleSelectValue = (propertyName, itemPropertyValueId) => {
        const nextValues = {...selectedValues, [propertyName] : itemPropertyValueId};
        const matchedVariant = findVariantByValues(item.variants, nextValues);

        setSelectedValues(nextValues);
        setHeroImage(matchedVariant?.images[0]?.url || item.image);
        setQuantity(1);
    };

    const handleThumbnailClick = (thumbnail) => {
        const nextValues = {};
        thumbnail.variantProperties.forEach((property) => {
            nextValues[property.name] = property.itemPropertyValueId;
        });

        setSelectedValues(nextValues);
        setHeroImage(thumbnail.url);
        setQuantity(1);
    };

    const isSoldOut = selectedVariant && selectedVariant.stockQuantity === 0;
    const canAddToCart = selectedVariant && !isSoldOut;

    const handleAddToCart = () => {
        if (!canAddToCart) {
            return;
        }

        addToCart({
            itemVariantId     : selectedVariant.itemVariantId,
            quantity          : quantity,
            itemUuid          : item.uuid,
            itemName          : item.name,
            itemImage         : selectedVariant.images[0]?.url || item.image,
            bruttoPrice       : item.bruttoPrice,
            currency          : item.currency,
            variantProperties : selectedVariant.properties,
            stockQuantity     : selectedVariant.stockQuantity
        });

        toast.success("Zum Warenkorb hinzugefügt!");
    };

    return (
        <div className="max-w-4xl mx-auto p-4">
            <div className="mb-4">
                <button
                    className="text-pegasusPurple hover:text-pink-700 font-medium"
                    onClick={() => navigate(-1)}
                >
                    &lt; Zurück
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                    <div className="w-full aspect-square bg-gray-100 rounded-lg overflow-hidden">
                        <ItemImage src={heroImage || item.image} alt={item.name} className="w-full h-full object-contain"/>
                    </div>

                    {thumbnails.length > 0 && (
                        <div className="flex gap-2 mt-3 overflow-x-auto">
                            {thumbnails.map((thumbnail) => (
                                <button
                                    key={thumbnail.itemVariantImageId}
                                    onClick={() => handleThumbnailClick(thumbnail)}
                                    className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 ${
                                        heroImage === thumbnail.url ? "border-pegasusPurple" : "border-transparent"
                                    }`}
                                >
                                    <img src={thumbnail.url} alt={item.name} className="w-full h-full object-cover"/>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div>
                    <h1 className="text-2xl font-bold text-gray-800">{item.name}</h1>
                    <p className="text-lg text-gray-600 mt-2">
                        {item.bruttoPrice.toFixed(2)} {item.currency.toUpperCase()}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">Nur Abholung im Studio – kein Versand.</p>
                    {item.description && (
                        <HtmlContent className="text-gray-600 mt-4" html={item.description}/>
                    )}

                    <div className="mt-6 space-y-4">
                        {propertyGroups.map((group) => (
                            <div key={group.name}>
                                <h2 className="text-sm font-semibold text-gray-700 mb-2">{group.name}</h2>
                                <div className="flex flex-wrap gap-2">
                                    {group.values.map(({itemPropertyValueId, value}) => (
                                        <button
                                            key={itemPropertyValueId}
                                            onClick={() => handleSelectValue(group.name, itemPropertyValueId)}
                                            className={`px-3 py-1 rounded-lg border text-sm ${
                                                selectedValues[group.name] === itemPropertyValueId
                                                    ? "border-pegasusPurple bg-pink-100 text-pegasusPurpleHover"
                                                    : "border-gray-300 text-gray-700 hover:border-pegasusPurple"
                                            }`}
                                        >
                                            {value}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-4">
                        {!selectedVariant ? (
                            <p className="text-sm text-gray-500">Bitte wähle eine Variante aus.</p>
                        ) : isSoldOut ? (
                            <p className="text-sm font-semibold text-gray-500">Ausverkauft</p>
                        ) : selectedVariant.stockQuantity <= 3 ? (
                            <p className="text-sm font-semibold text-pegasusPurpleHover">
                                Nur noch {selectedVariant.stockQuantity} verfügbar
                            </p>
                        ) : (
                            <p className="text-sm text-gray-500">Auf Lager</p>
                        )}
                    </div>

                    <div className="flex items-center gap-4 mt-4">
                        <label className="text-sm text-gray-700">
                            Menge
                            <input
                                type="number"
                                min={1}
                                max={selectedVariant?.stockQuantity || 1}
                                value={quantity}
                                disabled={!canAddToCart}
                                onChange={(e) => {
                                    const value = Number(e.target.value);
                                    const max = selectedVariant?.stockQuantity || 1;
                                    setQuantity(Math.min(Math.max(value, 1), max));
                                }}
                                className="ml-2 w-20 border border-gray-300 rounded-lg px-2 py-1"
                            />
                        </label>
                    </div>

                    <button
                        onClick={handleAddToCart}
                        disabled={!canAddToCart}
                        className={`w-full mt-6 py-2 rounded-lg font-semibold ${
                            canAddToCart
                                ? "bg-pegasusPurple text-white hover:bg-pegasusPurpleHover"
                                : "bg-gray-300 text-gray-600 cursor-not-allowed"
                        }`}
                    >
                        In den Warenkorb
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ShopItemPage;
